import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, TrashIcon, CalendarIcon } from './icons';
import { ContextMenu } from './ContextMenu';

const MAX_NOTE_LENGTH = 1000;

export interface LogEntryProps {
  message: string;
  authorInitials: string;
  authorName: string;
  timestamp: Date;
  /** compact=true → individual bordered card (embedded); compact=false → divider row (full log list) */
  compact?: boolean;
  /** Whether the entry has been edited after initial creation */
  isEdited?: boolean;
  /** If provided, an Edit item appears in the ⋮ menu. Called with the new message on save. */
  onSave?: (_newMessage: string) => Promise<void>;
  /** If provided, a Delete item appears in the ⋮ menu. Called after the user confirms deletion. */
  onDelete?: () => Promise<void>;
  /** If provided, a Set Reminder item appears in the ⋮ menu. */
  onRemind?: () => void;
}

export function LogEntry({
  message,
  authorInitials,
  authorName,
  timestamp,
  compact = false,
  isEdited = false,
  onSave,
  onDelete,
  onRemind,
}: LogEntryProps) {
  const { t, i18n } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (isEditing) autoResize();
  }, [isEditing, autoResize]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const relativeTime = useCallback((date: Date): string => {
    const diffMs = Date.now() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return t('logEntry.justNow');
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return t('logEntry.minutesAgo', { count: diffMin });
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return t('logEntry.hoursAgo', { count: diffHr });
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay === 1) return t('logEntry.yesterday');
    if (diffDay < 7) return t('logEntry.daysAgo', { count: diffDay });
    return date.toLocaleDateString(i18n.language);
  }, [t, i18n.language]);

  async function handleSave() {
    const trimmed = editText.trim();
    if (!trimmed || !onSave) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch (error) {
      console.error('[LogEntry] save error:', error);
      setSaveError(t('logEntry.editError'));
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancelEdit() {
    setIsEditing(false);
    setEditText(message);
    setSaveError(null);
  }

  async function handleConfirmDelete() {
    if (!onDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDelete();
    } catch {
      setDeleteError(t('logEntry.deleteError'));
      setIsDeleting(false);
    }
  }

  const containerClass = compact
    ? 'flex gap-3 rounded-sm border-thick border-green-200 bg-white px-4 py-3'
    : 'flex gap-3 border-b border-green-200 px-[18px] py-3 last:border-b-0';

  const hasMenu = (onSave || onDelete || onRemind) && !isEditing && !showDeleteConfirm;

  const menuItems = [
    onSave && {
      icon: <PencilIcon />,
      label: t('logEntry.editNote'),
      onClick: () => { setIsEditing(true); setEditText(message); },
    },
    onRemind && {
      icon: <CalendarIcon />,
      label: t('logEntry.remind'),
      onClick: onRemind,
    },
    onDelete && {
      icon: <TrashIcon />,
      label: t('logEntry.deleteNote'),
      danger: true as const,
      onClick: () => setShowDeleteConfirm(true),
    },
  ].filter(Boolean) as import('./ContextMenu').ContextMenuItem[];

  return (
    <div className={containerClass}>
      <div className="flex h-8 w-8 min-w-[32px] items-center justify-center rounded-full bg-green-800 font-body text-xs font-bold text-white">
        {authorInitials}
      </div>

      <div className="min-w-0 flex-1">
        {isEditing ? (
          <>
            <textarea
              ref={textareaRef}
              maxLength={MAX_NOTE_LENGTH}
              aria-label={t('logEntry.editNote')}
              value={editText}
              onChange={e => {
                setEditText(e.target.value);
                autoResize();
                if (saveError) setSaveError(null);
              }}
              disabled={isSaving}
              className="w-full resize-none overflow-hidden rounded border border-green-300 bg-green-50 p-2 font-body text-sm text-gray-900 outline-none focus:border-green-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {saveError && (
              <p className="mt-1 font-body text-xs text-red-600">{saveError}</p>
            )}
            <div className="mt-2 flex items-center gap-2">
              <span className="me-auto font-body text-xs text-gray-400">
                {editText.length}/{MAX_NOTE_LENGTH}
              </span>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="font-body text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                {t('logEntry.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !editText.trim()}
                className="rounded bg-green-700 px-2.5 py-1 font-body text-xs font-semibold text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('logEntry.save')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="whitespace-pre-wrap break-words font-body text-sm leading-snug text-gray-900">
              {message}
            </p>
            <p className="mt-1 font-body text-xs text-gray-500">
              {authorName} · {relativeTime(timestamp)}
              {isEdited && <span className="ms-1 italic">· {t('logEntry.edited')}</span>}
            </p>
            {showDeleteConfirm && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="font-body text-xs text-gray-600">{t('logEntry.deleteConfirm')}</span>
                {deleteError && (
                  <span className="font-body text-xs text-red-600">{deleteError}</span>
                )}
                <button
                  type="button"
                  onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                  disabled={isDeleting}
                  className="font-body text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  {t('logEntry.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="rounded bg-red-600 px-2.5 py-1 font-body text-xs font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('logEntry.deleteYes')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {hasMenu && (
        <div className="shrink-0 pt-0.5">
          <ContextMenu items={menuItems} triggerLabel={t('contextMenu.trigger')} />
        </div>
      )}
    </div>
  );
}
